// import node module libraries
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';

// import custom components
// import bootstrap icons
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import { useEffect, useState } from 'react';
import MatiereService from '../../services/MatiereService';
import AnneescolaireService from '../../services/AnneescolaireService';

const OffcanvasAddMatiereForm = ({ onClick }) => {
	const [validated, setValidated] = useState(false);
	const [selectedSection, setSelectedSection] = useState('');
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [currentSchoolYear, setCurrentSchoolYear] = useState(null);

	useEffect(() => {
		const fetchCurrentSchoolYear = async () => {
			try {
				const schoolYear = await AnneescolaireService.getCurrentAnneescolaire();
				setCurrentSchoolYear(schoolYear.data);
			} catch (error) {
				console.error(
					"Erreur lors de la récupération de l'année scolaire en cours:",
					error
				);
				alert(
					`Erreur lors de la récupération de l'année scolaire en cours. Détails de l'erreur : ${error.message}`
				);
			}
		};

		fetchCurrentSchoolYear();
	}, []);

	const SECTION_CHOICES = [
		{ value: 'en', label: 'Angolophone' },
		{ value: 'fr', label: 'Francophone' }
	];

	const handleSectionChange = (e) => {
		const value = e.target.value;
		setSelectedSection(value);
		// Reset feedback messages for points fields when section changes
		setFeedbackMessages({});
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const MatiereData = new FormData();

				// Add all your form data to the FormData
				MatiereData.append('nom', form.nom.value);
				MatiereData.append('section', form.section.value);

				if (selectedSection === 'fr') {
					MatiereData.append('points_16', form.points_16.value);
					MatiereData.append('points_17', form.points_17.value);
					MatiereData.append('points_18', form.points_18.value);
				} else if (selectedSection === 'en') {
					MatiereData.append('points_10', form.points_10.value);
					MatiereData.append('points_11', form.points_11.value);
					MatiereData.append('points_12', form.points_12.value);
				}

				MatiereData.append('annee_scolaire', currentSchoolYear.id);

				// Call the MatiereService to create the Matiere
				const response = await MatiereService.createMatiere(MatiereData);

				// Reset the form and state variables after successful creation
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Redirect the user to the list of Fraisscolarites
				window.location.href = '/pedagogie/matieres';
			} catch (error) {
				console.error('Erreur lors de la création de la matière', error);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<Form
			className="mb-3"
			noValidate
			validated={validated}
			onSubmit={handleSubmit}
		>
			{showAlert && (
				<Alert variant="danger">
					<ExclamationTriangleFill size={25} className="me-1" />
					{alertMessage}
				</Alert>
			)}
			<Row>
				<Col xs={12} className="mb-3">
					{/* Nom et prenom  */}
					<Form.Group>
						<Form.Label htmlFor="nom">
							Nom <span className="text-danger">*</span>
						</Form.Label>
						<Form.Control
							name="nom"
							type="text"
							placeholder="Saisir le nom de la Matiere"
							id="nom"
							required
							isInvalid={!!feedbackMessages['nom']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									nom: e.target.value ? '' : 'Le nom est requis.'
								}));
							}}
						/>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['nom']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Section */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="section">
							Section <span className="text-danger">*</span>
						</Form.Label>
						<Form.Select
							name="section"
							required
							isInvalid={!!feedbackMessages['section']}
							onChange={handleSectionChange} // Handle section change here
						>
							<option value="">Sélectionner la section</option>
							{SECTION_CHOICES.map((option, index) => (
								<option key={index} value={option.value}>
									{option.label}
								</option>
							))}
						</Form.Select>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['section']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Affichage des points pour les classes Francophones */}
				{selectedSection === 'fr' && (
					<Col xs={12} className="mb-3">
						<Form.Group>
							<Row>
								<Col xs={12} md={4} className="mb-3">
									<Form.Label htmlFor="points_16">
										Points CE2 <span className="text-danger">*</span>
									</Form.Label>
									<Form.Control
										name="points_16"
										type="number"
										placeholder="Points CE2"
										id="points_16"
										required
										isInvalid={!!feedbackMessages['points_16']}
										onChange={(e) => {
											setFeedbackMessages((prevMessages) => ({
												...prevMessages,
												points_16: e.target.value
													? ''
													: 'Les points sont requis.'
											}));
										}}
									/>
									<Form.Control.Feedback type="invalid">
										{feedbackMessages['points_16']}
									</Form.Control.Feedback>
								</Col>

								<Col xs={12} md={4} className="mb-3">
									<Form.Label htmlFor="points_17">
										Points CM1 <span className="text-danger">*</span>
									</Form.Label>
									<Form.Control
										name="points_17"
										type="number"
										placeholder="Points CM1"
										id="points_17"
										required
										isInvalid={!!feedbackMessages['points_17']}
										onChange={(e) => {
											setFeedbackMessages((prevMessages) => ({
												...prevMessages,
												points_17: e.target.value
													? ''
													: 'Les points sont requis.'
											}));
										}}
									/>
									<Form.Control.Feedback type="invalid">
										{feedbackMessages['points_17']}
									</Form.Control.Feedback>
								</Col>

								<Col xs={12} md={4} className="mb-3">
									<Form.Label htmlFor="points_18">
										Points CM2 <span className="text-danger">*</span>
									</Form.Label>
									<Form.Control
										name="points_18"
										type="number"
										placeholder="Points CM2"
										id="points_18"
										required
										isInvalid={!!feedbackMessages['points_18']}
										onChange={(e) => {
											setFeedbackMessages((prevMessages) => ({
												...prevMessages,
												points_18: e.target.value
													? ''
													: 'Les points sont requis.'
											}));
										}}
									/>
									<Form.Control.Feedback type="invalid">
										{feedbackMessages['points_18']}
									</Form.Control.Feedback>
								</Col>
							</Row>
						</Form.Group>
					</Col>
				)}

				{/* Affichage des points pour les classes Anglophones */}
				{selectedSection === 'en' && (
					<Col xs={12} className="mb-3">
						<Form.Group>
							<Row>
								<Col xs={12} md={4} className="mb-3">
									<Form.Label htmlFor="points_10">
										Points Class Four
										<span className="text-danger">*</span>
									</Form.Label>
									<Form.Control
										name="points_10"
										type="number"
										placeholder="Points Class Four"
										id="points_10"
										required
										isInvalid={!!feedbackMessages['points_10']}
										onChange={(e) => {
											setFeedbackMessages((prevMessages) => ({
												...prevMessages,
												points_10: e.target.value
													? ''
													: 'Les points sont requis.'
											}));
										}}
									/>
									<Form.Control.Feedback type="invalid">
										{feedbackMessages['points_10']}
									</Form.Control.Feedback>
								</Col>

								<Col xs={12} md={4} className="mb-3">
									<Form.Label htmlFor="points_11">
										Points Class Five <span className="text-danger">*</span>
									</Form.Label>
									<Form.Control
										name="points_11"
										type="number"
										placeholder="Points Class Five"
										id="points_11"
										required
										isInvalid={!!feedbackMessages['points_11']}
										onChange={(e) => {
											setFeedbackMessages((prevMessages) => ({
												...prevMessages,
												points_11: e.target.value
													? ''
													: 'Les points sont requis.'
											}));
										}}
									/>
									<Form.Control.Feedback type="invalid">
										{feedbackMessages['points_11']}
									</Form.Control.Feedback>
								</Col>

								<Col xs={12} md={4} className="mb-3">
									<Form.Label htmlFor="points_12">
										Points Class Six
										<span className="text-danger">*</span>
									</Form.Label>
									<Form.Control
										name="points_12"
										type="number"
										placeholder="Points Class Six"
										id="points_12"
										required
										isInvalid={!!feedbackMessages['points_12']}
										onChange={(e) => {
											setFeedbackMessages((prevMessages) => ({
												...prevMessages,
												points_12: e.target.value
													? ''
													: 'Les points sont requis.'
											}));
										}}
									/>
									<Form.Control.Feedback type="invalid">
										{feedbackMessages['points_12']}
									</Form.Control.Feedback>
								</Col>
							</Row>
						</Form.Group>
					</Col>
				)}

				{/* Buttons */}
				<Col xs={12} className="mt-4">
					<Button variant="success" type="submit">
						Enregistrer
					</Button>
					<Button onClick={onClick} variant="outline-danger" className="ms-2">
						Fermer
					</Button>
				</Col>
			</Row>
		</Form>
	);
};

export default OffcanvasAddMatiereForm;
